import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Alert, AlertTitle, Grid2 } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { User } from '@thro-types/users';

import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import {
  useGetElectionById,
  useGetElegibleVotersForElectionCount,
  useDeleteElectionMutation
} from 'api/hooks/polyas/polyas';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';

import { getStatusLabel } from '../../MyElections/helper';
import { ElectionDetailSkeleton } from '../components/election-skeletons';
import { VoterImport } from '../components/voter-import';
import { VoterManagement } from '../components/voter-management';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface ElectionDetailPageProps {
  currentUser: User;
}

export default function ElectionDetailPage({ currentUser }: ElectionDetailPageProps) {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const location = useLocation();

  const { data, isLoading, error } = useGetElectionById(id);
  const { data: votersData, error: votersError } = useGetElegibleVotersForElectionCount(id);
  const { deleteElectionMutation } = useDeleteElectionMutation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  usePageHeading({
    heading: t(translations.pages.polyas.detail.backToElectionAdmin()),
    headingProps: { back: location.state?.back ?? '/election/admin' }
  });

  if (error || votersError) {
    return (
      <Alert severity="error">
        <AlertTitle>{t(translations.common.genericError())}</AlertTitle>
        {t(translations.pages.polyas.detail.errorLoadingElection())}
      </Alert>
    );
  }

  if (!data || !votersData || isLoading) {
    return <ElectionDetailSkeleton />;
  }

  const votedCount = votersData?.votedCount ?? 0;
  const progressPercent =
    !!votersData && votersData?.count > 0 ? (votedCount / votersData.count) * 100 : 0;

  const handleDelete = () => {
    deleteElectionMutation.mutate(
      { id },
      {
        onSuccess: () => {
          navigate('/election/admin');
        }
      }
    );
  };

  const statusColorMap: Record<string, 'success' | 'warning' | 'default'> = {
    active: 'success',
    upcoming: 'warning',
    ended: 'default'
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }} pb={'2em'}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Grid2 container direction={'column'} spacing={1} wrap="nowrap">
          <Grid2 container alignItems="center" spacing={1} wrap="nowrap">
            <Typography variant="h4" color="text.primary">
              {data?.title}
            </Typography>
            <Chip
              label={getStatusLabel(data?.status, t)}
              color={statusColorMap[data?.status]}
              size="small"
            />
          </Grid2>
          <Typography variant="body2" color="text.secondary">
            {t(translations.pages.polyas.detail.createdBy())} {data.createdBy} am{' '}
            {format(new Date(data.createdAt), 'dd.MM.yyyy', { locale: de })}
          </Typography>
        </Grid2>
        <Grid2 container spacing={2} justifyContent={'flex-end'}>
          <Tooltip
            title={
              data?.status !== 'future'
                ? t(translations.pages.polyas.edit.notEditableTooltip())
                : ''
            }
            arrow
          >
            <span>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                disabled={data?.status !== 'future'}
                onClick={() =>
                  navigate(`/election/admin/edit/${id}`, {
                    state: { back: `/election/admin/${id}` }
                  })
                }
              >
                {t(translations.pages.polyas.detail.edit())}
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              data?.status !== 'future'
                ? t(translations.pages.polyas.detail.deleteNotAllowedTooltip())
                : ''
            }
            arrow
          >
            <span>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                disabled={data?.status !== 'future'}
                onClick={() => setDeleteDialogOpen(true)}
              >
                {t(translations.pages.polyas.detail.delete())}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<OpenInNewIcon />}
            href={`https://election.polyas.com/${data?.polyasElectionHash}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t(translations.pages.polyas.detail.goToElectionPage())}
          </Button>
        </Grid2>
      </Box>

      <Box
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}
      >
        <Card>
          <CardContent>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'text.secondary' }}
            >
              <CalendarTodayIcon fontSize="small" />
              <Typography variant="body2">
                {t(translations.pages.polyas.detail.electionPeriod())}
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight={500}>
              {format(new Date(data?.electionStartDate), 'dd.MM.yyyy HH:mm', { locale: de })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t(translations.common.end())}
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {format(new Date(data?.electionEndDate), 'dd.MM.yyyy HH:mm', { locale: de })}
            </Typography>
          </CardContent>
        </Card>
        {data?.visibleStartDate && data?.visibleEndDate && (
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                  color: 'text.secondary'
                }}
              >
                <VisibilityIcon fontSize="small" />
                <Typography variant="body2">
                  {t(translations.pages.polyas.detail.visibility())}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={500}>
                {format(new Date(data?.visibleStartDate), 'dd.MM.yyyy', { locale: de })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(translations.common.end())}
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {format(new Date(data?.visibleEndDate), 'dd.MM.yyyy', { locale: de })}
              </Typography>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'text.secondary' }}
            >
              <GroupIcon fontSize="small" />
              <Typography variant="body2">
                {t(translations.pages.polyas.detail.voterParticipation())}
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={600}>
              {votedCount} / {votersData?.count ?? 0}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{ mt: 1, mb: 0.5, borderRadius: 1, height: 6 }}
            />
            <Typography variant="caption" color="text.secondary">
              {progressPercent.toFixed(1)}% {t(translations.pages.polyas.detail.votedCount())}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Box>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label={t(translations.pages.polyas.detail.overview())} />
          <Tab
            label={`${t(translations.pages.polyas.detail.eligibleVoters())} (${
              votersData?.count ?? 0
            })`}
          />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t(translations.pages.polyas.detail.description())}
              </Typography>
              {data.description ? (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {data.description}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  {t(translations.pages.polyas.detail.noDescription())}
                </Typography>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t(translations.pages.polyas.detail.importVoters())}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t(translations.pages.polyas.detail.importVotersDescription())}
              </Typography>
              {data?.status === 'future' ? (
                <VoterImport electionId={id} />
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t(translations.pages.polyas.detail.importNotAllowedTooltip())}
                </Alert>
              )}

              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                {t(translations.pages.polyas.detail.manageVoters())}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t(translations.pages.polyas.detail.manageVotersDescription())}
              </Typography>
              <VoterManagement
                electionId={id}
                totalVotersCount={votersData.count ?? 0}
                votedCount={votedCount}
                pendingVotersCount={votersData.count ?? 0 - votedCount}
              />
            </CardContent>
          </Card>
        </TabPanel>
      </Box>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title={t(translations.pages.polyas.detail.delete())}
        confirmButtonText={t(translations.pages.polyas.detail.delete())}
        isPending={deleteElectionMutation.isPending}
      >
        {t(translations.pages.polyas.detail.deleteConfirm())}
      </ConfirmationDialog>
    </Box>
  );
}
